import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { GoogleSignInButton } from "@/components/landing/GoogleSignInButton";
import { MicrosoftSignInButton } from "@/components/landing/MicrosoftSignInButton";
import { MagneticButton } from "@/components/motion/MagneticButton";
import { SuccessBloom } from "@/components/motion/SuccessBloom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useToast } from "@/hooks/use-toast";
import { useConversionTracking } from "@/hooks/useConversionTracking";
import { useAnalyticsTracking } from "@/hooks/useAnalyticsTracking";
import { STRIPE_PLANS } from "@/lib/stripe-config";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import autopitchLogo from "@/assets/autopitch-logo.png";

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Default mode when opening. */
  defaultMode?: "signin" | "signup";
  /** Optional plan key to checkout into after signup. */
  pendingPlan?: keyof typeof STRIPE_PLANS | null;
}

export function AuthDialog({ open, onOpenChange, defaultMode = "signup", pendingPlan }: AuthDialogProps) {
  const [isLogin, setIsLogin] = useState(defaultMode === "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [bloomKey, setBloomKey] = useState<number | null>(null);
  const { toast } = useToast();
  const { track } = useConversionTracking();
  const { track: trackAnalytics } = useAnalyticsTracking();
  const { checkout } = useStripeCheckout();

  useEffect(() => {
    if (open) setIsLogin(defaultMode === "signin");
  }, [open, defaultMode]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event !== "SIGNED_IN" || !session?.user) return;

      // Detect OAuth signup (vs email — which is handled inline in handleSubmit)
      const provider =
        (session.user.app_metadata?.provider as string | undefined) ?? "email";
      const isOAuth = provider === "google" || provider === "azure" || provider === "microsoft";

      if (isOAuth) {
        const isNewSignup =
          session.user.created_at &&
          Date.now() - new Date(session.user.created_at).getTime() < 60_000;
        const method = provider === "azure" ? "microsoft" : provider;
        if (isNewSignup) {
          track("signup_complete");
          trackAnalytics("signup_completed", { method });
        }
        trackAnalytics("signin_completed", { method });
        setBloomKey(Date.now());
        setTimeout(() => onOpenChange(false), 600);
      }

      if (pendingPlan) {
        const plan = STRIPE_PLANS[pendingPlan];
        if (plan) setTimeout(() => checkout(plan.price_id, "subscription"), 500);
      }
    });
    return () => subscription.unsubscribe();
  }, [pendingPlan, checkout, onOpenChange, track, trackAnalytics]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        track("signup_start");
        trackAnalytics("signup_started", { method: "email" });
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName },
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        track("signup_complete");
        trackAnalytics("signup_completed", { method: "email" });
        setBloomKey(Date.now());
        toast({
          title: "Account created",
          description: "Check your email for a confirmation link to complete your registration.",
        });
      }
    } catch (err: any) {
      const raw = err?.message || String(err);
      const isNetwork =
        raw === "Load failed" ||
        raw.toLowerCase().includes("failed to fetch") ||
        raw.toLowerCase().includes("network");
      toast({
        title: isNetwork ? "Network issue" : "Sign in failed",
        description: isNetwork
          ? "We couldn't reach the server. Disable VPN / iCloud Private Relay or content blockers, then try again."
          : raw,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    const { error } = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
  };

  const handleMicrosoft = async () => {
    const { error } = await lovable.auth.signInWithOAuth("microsoft", {
      redirect_uri: window.location.origin,
    });
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px] p-0 overflow-hidden border-border bg-card">
        <div className="relative p-6 space-y-5">
          <SuccessBloom triggerKey={bloomKey} hue="success" tier={3} />
          <DialogHeader className="space-y-2 text-center">
            <img src={autopitchLogo} alt="Auto Pitch" className="h-10 w-10 object-contain rounded-lg mx-auto" />
            <DialogTitle className="text-base font-semibold">
              {isLogin ? (
                "Welcome back"
              ) : pendingPlan ? (
                `Sign up for ${STRIPE_PLANS[pendingPlan]?.name ?? ""}`
              ) : (
                <>Start your <span className="text-gradient-brand">outbound engine</span></>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {isLogin
                ? "Sign in to your dashboard"
                : pendingPlan
                ? "Create your account, then complete payment"
                : "Your first qualified meeting is free. No card required."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-3">
            {!isLogin && (
              <div className="space-y-1.5">
                <Label className="text-xs">Full name</Label>
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Smith"
                  className="bg-muted/30 border-border h-9"
                  required
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@company.com"
                className="bg-muted/30 border-border h-9"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-muted/30 border-border h-9"
                required
                minLength={6}
              />
            </div>

            <MagneticButton strength={4} className="w-full">
              <Button type="submit" className="w-full glow-primary h-9" disabled={loading}>
                {loading ? "Loading…" : isLogin ? "Sign in" : "Create account"}
              </Button>
            </MagneticButton>

            <div className="flex items-center gap-3">
              <Separator className="flex-1" />
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">or</span>
              <Separator className="flex-1" />
            </div>

            <GoogleSignInButton onClick={handleGoogle} disabled={loading} />
            <MicrosoftSignInButton onClick={handleMicrosoft} disabled={loading} />

            <p className="text-xs text-center text-muted-foreground pt-1">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary hover:underline"
              >
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
